/**
 * Formatacao localizada.
 *
 * A interface e escrita em portugues do Brasil diretamente no codigo (decisao
 * do produto: um so idioma, sem framework de i18n). O FORMATO de numeros, datas
 * e moeda fica aqui, num so lugar, para nenhuma tela precisar de saber como se
 * escreve um valor — e para ninguem ser tentado a formatar a mao.
 *
 * `LOCALE` e uma constante, nao uma escolha. Enquanto houve um preset de
 * Portugal ao lado deste, havia duas formas de a mesma data aparecer e nada
 * que garantisse que as telas concordavam entre si. O produto e do Brasil:
 * real, fuso de Sao Paulo, portugues do Brasil.
 */

export interface LocaleConfig {
  /** BCP-47. */
  locale: string;
  /** ISO-4217. */
  currency: string;
  /** IANA. */
  timeZone: string;
}

export const DEFAULT_LOCALE: LocaleConfig = {
  locale: 'pt-BR',
  currency: 'BRL',
  timeZone: 'America/Sao_Paulo',
};

/**
 * O idioma e a moeda do aplicativo, para quem precisa de os nomear (o atributo
 * `lang`, o reconhecimento de voz, as notificacoes). Sempre esta constante —
 * nunca uma cadeia escrita a mao no meio de um componente.
 */
export const LOCALE = DEFAULT_LOCALE.locale;

let active: LocaleConfig = DEFAULT_LOCALE;

export function setLocaleConfig(config: LocaleConfig): void {
  active = config;
  formatterCache.clear();
}

export function getLocaleConfig(): LocaleConfig {
  return active;
}

const formatterCache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function cached<T extends Intl.NumberFormat | Intl.DateTimeFormat>(
  key: string,
  make: () => T,
): T {
  const hit = formatterCache.get(key);
  if (hit) return hit as T;
  const made = make();
  formatterCache.set(key, made);
  return made;
}

/** Formata um valor monetario, ex. 1234.5 -> 'R$ 1.234,50'. */
export function money(value: number, currency = active.currency): string {
  const fmt = cached(`money:${active.locale}:${currency}`, () =>
    new Intl.NumberFormat(active.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  ) as Intl.NumberFormat;
  return fmt.format(value);
}

/**
 * Valores monetarios sao salvos em cents (inteiros) para evitar erros de
 * virgula flutuante em somas de centenas de transacoes.
 */
export function centsToMoney(cents: number, currency = active.currency): string {
  return money(cents / 100, currency);
}

export function toCents(value: number): number {
  return Math.round(value * 100);
}

export function fromCents(cents: number): number {
  return cents / 100;
}

/** '12 de marco de 2026' */
export function longDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const fmt = cached(`long:${active.locale}`, () =>
    new Intl.DateTimeFormat(active.locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: active.timeZone,
    }),
  ) as Intl.DateTimeFormat;
  return fmt.format(d);
}

/** '12/03/2026' */
export function shortDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const fmt = cached(`short:${active.locale}`, () =>
    new Intl.DateTimeFormat(active.locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: active.timeZone,
    }),
  ) as Intl.DateTimeFormat;
  return fmt.format(d);
}

/** '14:30' */
export function time(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const fmt = cached(`time:${active.locale}`, () =>
    new Intl.DateTimeFormat(active.locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: active.timeZone,
    }),
  ) as Intl.DateTimeFormat;
  return fmt.format(d);
}

/**
 * 'ha 5 minutos', 'ontem', 'daqui a 2 dias'.
 * Preferimos isto a datas absolutas em avisos — le-se melhor em voz alta.
 */
export function relative(date: Date | string, from: Date = new Date()): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = d.getTime() - from.getTime();
  const abs = Math.abs(diffMs);
  const minute = 60_000;
  const hour = 3_600_000;
  const day = 86_400_000;

  if (abs < minute) return 'agora mesmo';

  const fmt = cached(`rel:${active.locale}`, () =>
    // Intl.RelativeTimeFormat nao partilha o tipo dos outros; guardamos a parte
    // e recriamos — o custo e desprezavel comparado com o resto da tela.
    new Intl.NumberFormat(active.locale),
  );
  void fmt;

  const rtf = new Intl.RelativeTimeFormat(active.locale, { numeric: 'auto' });
  if (abs < hour) return rtf.format(Math.round(diffMs / minute), 'minute');
  if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  if (abs < day * 30) return rtf.format(Math.round(diffMs / day), 'day');
  if (abs < day * 365) return rtf.format(Math.round(diffMs / (day * 30)), 'month');
  return rtf.format(Math.round(diffMs / (day * 365)), 'year');
}

/** Nome do mes por extenso, ex. 'marco'. Usado nos relatorios. */
export function monthName(monthIndex: number): string {
  const d = new Date(2000, monthIndex, 1);
  return new Intl.DateTimeFormat(active.locale, {
    month: 'long',
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Interpreta numeros escritos como o usuário fala/escreve em portugues:
 * '1.234,56' -> 1234.56, '12,50' -> 12.5, 'R$ 43,20' -> 43.2.
 * Necessario porque a entrada por voz devolve texto livre.
 */
export function parseNumber(input: string): number | null {
  const cleaned = input
    .replace(/[^\d.,-]/g, '')
    .trim();
  if (!cleaned) return null;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalized: string;
  if (lastComma > lastDot) {
    // Virgula e o separador decimal: remover pontos de milhar.
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    // Ponto e o separador decimal: remover virgulas de milhar.
    normalized = cleaned.replace(/,/g, '');
  } else {
    normalized = cleaned;
  }

  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}
